import { all, call, delay, put, race, select, takeEvery, takeLatest } from '@redux-saga/core/effects';
import { PayloadAction } from "@reduxjs/toolkit";
import { signOut } from "aws-amplify/auth";
import posthog from '../../components/AnalyticsProvider/client';
import { isCurrentRoutePublic, nextPage, prevPage } from "../../util/library/Router";
import { AccountService } from "../../util/services/account";
import { VesselSideService } from "../../util/services/vessel-side";
import { selectIsDashboardEnabled, selectPermission, selectUser, selectVesselsSide } from "../../util/store/selectors";
import { KEY_STORAGE, PATHNAME, timeRequestSetting } from "../../util/varibles/constants";
import { getSetting } from "../../util/varibles/defaultSetting";
import { showErrorResponse } from "../../util/varibles/global";
import { IOperation } from "../../util/varibles/interface";
import { generateActions, getViewMode, VIEW_MODE } from "../../util/varibles/permission";
import { PreferenceSetting } from "../../util/varibles/userSetting";
import { IUser, IVesselOfUser, } from "./constants";
import { loginActions } from "./reducer";

function* updateUserSettingWatcher({ ...params }): Generator<any, any, IUser> {
    try {
        const user = yield select(selectUser);
        const [setting, args] = params.payload;
        const { default_vessel } = args || user;
        yield delay(timeRequestSetting);
        yield call(AccountService.updateSetting, {
            id: user.user_id,
            setting: {
                ...user.setting || {},
                ...setting
            },
            default_vessel
        });
        yield put(loginActions.updateLoadingUserSetting());
    } catch (error) {
        showErrorResponse('Update setting failure', error);
        yield put(loginActions.updateLoadingUserSetting());
    }
}

function checkUser(user?: IUser) {
    if (!user) throw Error('Missing data')
}

function* updateUser(): Generator<any, any, any> {
    const user = yield call(AccountService.info);
    checkUser(user);
    const { setting = {}, roles = [] } = user;
    const permission = generateActions(roles);
    yield put(loginActions.getUserSuccess({ ...user, permission }));
    const mode = getSetting(setting, PreferenceSetting.VIEW_MODE);
    const realMode = getViewMode(permission, mode);
    yield put(loginActions.updateLoadingUser(false));
    if (realMode !== mode) {
        yield put(loginActions.updateUserSetting([{ [PreferenceSetting.VIEW_MODE]: realMode }]));
    }
    const theme = getSetting(setting, PreferenceSetting.THEME_COLOR);
    localStorage.setItem(KEY_STORAGE.THEME, theme);
    if (process.env.REACT_APP_POSTHOG_KEY && user.email) {
        try {
            posthog.identify(user.email, {
                tenant_name: user.tenant?.name || user.tenant_name,
            });
        } catch (error) {
            console.warn('PostHog identify failed', error);
        }
    }
}

function* getUserWatcher(args: PayloadAction<{ action?: Function }>): Generator<any, any, any> {
    try {
        yield put(loginActions.updateLoadingUser(true));
        yield updateUser();
        const { action } = args.payload || {};
        if (action)
            action()
    } catch (error) {
        showErrorResponse('Get user information failure', error);
        yield put(loginActions.logout());
    }
}

function* logoutWatcher(): Generator {
    if (process.env.REACT_APP_POSTHOG_KEY) {
        try {
            posthog.reset();
        } catch (error) {
            console.warn('PostHog reset failed', error);
        }
    }
    try {
        yield delay(200);
        yield race({
            signOut: call(() => signOut({ global: true })),
            timeout: delay(5000)
        });
    } catch (error) {
        showErrorResponse('Logout failed', error);
    } finally {
        localStorage.clear();
        if (!isCurrentRoutePublic()) {
            nextPage(PATHNAME.LOGIN);
        }
    }
}

function* loginWatcher(): Generator<any, any, any> {
    try {
        yield put(loginActions.updateLoadingUser(true));
        yield updateUser();
        prevPage();
    } catch (error) {
        showErrorResponse('Login failed', error);
        yield put(loginActions.logout());
    }
}

function* getDomainsWatcher(): Generator {
    try {
        const domains = yield call(AccountService.domain);
        yield put(loginActions.getDomainsSuccess(domains));
        return domains
    } catch (error) {
        showErrorResponse('Load login configuration failed', error);
    }
}

function* getOperationsVesselSideWatcher(): Generator {
    try {
        yield delay(200);
        yield put(loginActions.getOpsOfVesselRequest());
        const vessels = yield call(VesselSideService.dashboard, { vessels: [] });
        yield put(loginActions.getOpsOfVesselSuccess(vessels));
    } catch (error) {
        yield put(loginActions.getOpsOfVesselFailure());
    }
}

function* deleteOpTrackingWatcher(args: PayloadAction<string[]>): Generator<any, any, IVesselOfUser[]> {
    try {
        const vessels = yield select(selectVesselsSide);
        const ids = new Set(args.payload);
        const isExist = vessels.some(vessel => vessel.operations.some(sub => ids.has(sub.operation.id)));
        const isAllow = yield select(selectIsDashboardEnabled);
        if (isExist && isAllow)
            yield put(loginActions.getOpsOfVessel())
    } catch (e) {
        showErrorResponse('Load data failed', e);
    }
}

function* updateOpsTrackingWatcher({ ...args }): Generator<{}, any, IVesselOfUser[]> {
    try {
        const permission: any = yield select(selectPermission);
        if (!permission[VIEW_MODE.VESSEL])
            return;

        const operations = args.payload;
        const vessels = yield select(selectVesselsSide);
        const isRefresh = operations.some((item: IOperation) => {
            const { id, status, vessel_id } = item.operation;
            const { operations = [] } = vessels.find(vessel => vessel.id === vessel_id) || {};
            return operations.length === 0 || operations.some(sub => sub.operation.id === id && sub.operation.status !== status);
        });
        const isAllow = yield select(selectIsDashboardEnabled);
        if (isRefresh && isAllow)
            yield put(loginActions.getOpsOfVessel())
    } catch (e) {
        console.log(e);
        showErrorResponse('Load data failed', e);
    }
}

export default function* loginSaga() {
    yield all([
        takeLatest(loginActions.updateUserSetting.type, updateUserSettingWatcher),
        takeLatest(loginActions.logout.type, logoutWatcher),
        takeEvery(loginActions.getUserRequest.type, getUserWatcher),
        takeEvery(loginActions.loginRequest.type, loginWatcher),
        takeEvery(loginActions.getDomainsRequest.type, getDomainsWatcher),
        takeLatest(loginActions.getOpsOfVessel.type, getOperationsVesselSideWatcher),
        takeEvery(loginActions.deleteOpOfVessel.type, deleteOpTrackingWatcher),
        takeEvery(loginActions.updateOpsOfVessel.type, updateOpsTrackingWatcher)
    ])
}
